import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BookFreeVisit from "@/pages/BookFreeVisit";
import EstimateCalculator from "@/pages/EstimateCalculator";
import { captureUtm } from "@/lib/utm";

function parseSalesIQ(raw) {
  if (!raw) return null;
  // full snippet or URL with wc= param (zohopublic format)
  const wcMatch = raw.match(/https:\/\/salesiq\.[a-z.]+\/widget\?wc=[a-zA-Z0-9_-]+/i);
  if (wcMatch) return { src: wcMatch[0] };
  // modern format: widgetcode in $zoho.salesiq config
  const codeMatch = raw.match(/widgetcode["'\s:=]+["']?([a-zA-Z0-9_-]+)["']?/i);
  if (codeMatch) return { code: codeMatch[1] };
  // bare code pasted alone
  const bare = raw.trim();
  if (/^[a-zA-Z0-9_-]{10,}$/.test(bare)) {
    return { src: `https://salesiq.zohopublic.com/widget?wc=${bare}` };
  }
  return null;
}

function SalesIQLoader() {
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/public`)
      .then((r) => r.json())
      .then((cfg) => {
        if (!cfg?.salesiq_enabled || !cfg.salesiq_widget_code) return;
        if (document.getElementById("zsiqscript")) return;
        const parsed = parseSalesIQ(cfg.salesiq_widget_code);
        if (!parsed) return;
        window.$zoho = window.$zoho || {};
        window.$zoho.salesiq = window.$zoho.salesiq || { values: {}, ready: function () {} };
        if (parsed.code) window.$zoho.salesiq.widgetcode = parsed.code;
        const script = document.createElement("script");
        script.id = "zsiqscript";
        script.type = "text/javascript";
        script.defer = true;
        script.src = parsed.src || "https://salesiq.zoho.com/widget";
        document.head.appendChild(script);
      })
      .catch(() => {});
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    captureUtm();
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App bg-sand-bg text-foreground font-sans antialiased">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/book-free-visit" element={<BookFreeVisit />} />
          <Route path="/estimate-calculator" element={<EstimateCalculator />} />
          <Route path="/book" element={<Navigate to="/book-free-visit" replace />} />
          <Route path="/estimate" element={<Navigate to="/estimate-calculator" replace />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </BrowserRouter>
      <SalesIQLoader />
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
